import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface PageHeaderProps {
  title: string;
  description?: string;
  actions?: ReactNode;
  /** Contenu secondaire : filtres, onglets, indicateurs de contexte. */
  toolbar?: ReactNode;
  className?: string;
}

/**
 * En-tête d'écran.
 *
 * Collant en haut de la zone de contenu : sur des listes longues, les actions
 * principales restent atteignables sans remonter, ce qui compte pour une
 * application utilisée toute la journée (§19).
 */
export function PageHeader({
  title,
  description,
  actions,
  toolbar,
  className,
}: PageHeaderProps): React.JSX.Element {
  return (
    <div
      className={cn(
        'no-print sticky top-0 z-10 border-b border-line-subtle bg-canvas/85 backdrop-blur-md',
        className,
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3 px-6 pt-5 pb-4">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold tracking-tight text-ink">{title}</h1>
          {description && <p className="mt-0.5 text-xs text-ink-faint">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {toolbar && <div className="px-6 pb-4">{toolbar}</div>}
    </div>
  );
}

/** Conteneur de contenu, largeur et rythme verticaux constants d'un écran à l'autre. */
export function PageBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return <div className={cn('space-y-5 px-6 py-5', className)}>{children}</div>;
}
